/* ============================================================
   iapLogger.ts — Structured logging for the IAP layer.
   ------------------------------------------------------------
   Why a dedicated logger?

   TestFlight builds have no terminal: `console.log` lands in
   the iOS Console.app only when the device is wired to a Mac
   with Console.app open and filtered by "CADtomie". That's a
   poor debug story when a reviewer runs into a purchase issue.

   This logger:
     • prefixes every line with `[iap]` and a millis timestamp
     • keeps the last 200 events in an in-memory ring buffer
       so a "Diagnostics" panel or a Sentry breadcrumb stream
       can dump them later
     • forwards to console at all log levels (Safari Web
       Inspector attached to the TestFlight build picks these up
       — invaluable for live debugging on physical hardware)
     • is intentionally trivial: no transport, no batching, no
       network. Purchase flows are too sensitive to lose telemetry
       behind queuing.
   ============================================================ */

export type IapLogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface IapLogEntry {
  ts: number;
  level: IapLogLevel;
  msg: string;
  ctx?: Record<string, unknown>;
}

const RING_CAPACITY = 200;
const _ring: IapLogEntry[] = [];

function _push(entry: IapLogEntry): void {
  _ring.push(entry);
  if (_ring.length > RING_CAPACITY) _ring.shift();
}

function _consoleFn(level: IapLogLevel): (...args: unknown[]) => void {
  switch (level) {
    case 'error': return console.error.bind(console);
    case 'warn':  return console.warn.bind(console);
    case 'info':  return console.info.bind(console);
    default:      return console.log.bind(console);
  }
}

function _log(level: IapLogLevel, msg: string, ctx?: Record<string, unknown>): void {
  const entry: IapLogEntry = { ts: Date.now(), level, msg, ctx };
  _push(entry);
  const args: unknown[] = [`[iap] ${msg}`];
  if (ctx) args.push(ctx);
  _consoleFn(level)(...args);
}

export const iapLog = {
  debug: (msg: string, ctx?: Record<string, unknown>) => _log('debug', msg, ctx),
  info:  (msg: string, ctx?: Record<string, unknown>) => _log('info',  msg, ctx),
  warn:  (msg: string, ctx?: Record<string, unknown>) => _log('warn',  msg, ctx),
  error: (msg: string, ctx?: Record<string, unknown>) => _log('error', msg, ctx),
};

/** Return a shallow copy of the in-memory log so a diagnostics view
 *  can render it without risking concurrent-mutation surprises. */
export function getIapLogSnapshot(): IapLogEntry[] {
  return _ring.slice();
}

/** Clear the buffer — used by a "Reset diagnostics" button if exposed. */
export function clearIapLog(): void {
  _ring.length = 0;
}
